import { axiosPlacement } from "./AxiosInstance.js"
import { getCached, invalidateCache } from "./dataCache";

export const getContacts = async (limit = 10, page = 1, filters = {}) => {
  const fetcher = async () => {
    // Always use the search endpoint to get enriched data (organizations, assigned users)
    // as the regular GET endpoint doesn't include relation attachments.
    const response = await axiosPlacement.post('/contacts/searchContacts', {
      ...filters,
      limit,
      page: page
    });
    return response.data;
  };

  return fetcher();
};

export const searchContacts = async (body) => {
  try {
    const response = await axiosPlacement.post('/contacts/searchContacts', body);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    throw new Error(`Failed to search contacts: ${error.response?.data?.message || error.message}`);
  }
};

// Create new contact with enhanced error handling
export const createContact = async (backendPayload) => {
  const allowedFields = [
    "name",
    "email",
    "phone",
    "organizationId",
    "assignedToId",
    "createdById",
    "updatedById"
    // "status" only if schema supports it
  ];

  const filteredPayload = Object.fromEntries(
    Object.entries(backendPayload).filter(([key]) => allowedFields.includes(key))
  );

  try {
    const response = await axiosPlacement.post('/contacts', filteredPayload);
    invalidateCache();
    return response.data;
  } catch (error) {
  }
};


// Other service methods with similar error handling pattern...
export const getContactById = async (id) => {
  try {
    const response = await axiosPlacement.get(`/contacts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Fetch contact error:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to fetch contact: ${error.response?.data?.message || error.message}`);
  }
};

export const updateContact = async (id, backendPayload) => {
  try {
    const response = await axiosPlacement.put(`/contacts/${id}`, backendPayload);
    invalidateCache();
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    throw new Error(`Failed to update contact: ${error.response?.data?.message || error.message}`);
  }
};

export const replaceContact = async (id, backendPayload) => {
  try {
    const response = await axiosPlacement.put(`/contacts/${id}`, backendPayload);
    invalidateCache();
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    throw new Error(`Failed to replace contact: ${error.response?.data?.message || error.message}`);
  }
};

export const deleteContact = async (id) => {
  try {
    await axiosPlacement.delete(`/contacts/${id}`);
    invalidateCache();
    return id;
  } catch (error) {
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    throw new Error(`Failed to delete contact: ${error.response?.data?.message || error.message}`);
  }
};

export const getContactOptions = async () => {
  try {
    const response = await axiosPlacement.get('/contactOptions');
    const data = response.data;
    
    const convertToOptions = (str) => {
      if (!str) return [];
      return str.split(',').map(item => ({
        value: item.trim(),
        label: item.trim()
      }));
    };

    return {
      salutations: convertToOptions(data.salutations),
      assignedTo: convertToOptions(data.assignedTo),
    };
  } catch (error) {
    console.error('Contact options fetch failed:', error);
    
    if (error.code === 'ECONNRESET') {
      throw new Error(`Network connection failed. Please check your connection and try again.`);
    }
    
    throw new Error(`Failed to fetch contact options: ${error.response?.data?.message || error.message}`);
  }
};

export const getHRPostedJobs = async (hrId, page = 1, limit = 10) => {
  try {
    const response = await axiosPlacement.get(`/contacts/hrposted`, {
      params: {
        hrId,
        page,
        limit
      }
    });
    return response.data;
  } catch (error) {
    console.error('Fetch HR posted jobs error:', error);
    throw new Error(`Failed to fetch HR posted jobs: ${error.response?.data?.message || error.message}`);
  }
};
